/**
 * UAT-B2 (frame 1d) — the one-time Today resurface of the skipped 13–15
 * guardian-invite beat. Self-contained (the TeamInviteBanner precedent):
 * fetches its own signals and returns null unless the athlete is a derived
 * minor who skipped the beat, has no guardian, hasn't dismissed, and is in a
 * session newer than the skip. Renders BELOW the hero — never the top slot.
 *
 * No-nagging contract: "Not now" stamps `_guardian_nudge_dismissed_at` and the
 * card NEVER returns; no badge, no bell, no email; at most one resurface.
 */

import { useState } from 'react';
import { useAthleteContext } from '../../hooks/useAthleteContext';
import { useRelationships } from '../../hooks/useRelationships';
import { useUserTier } from '../../hooks/useUserTier';
import { useSettingsPersist } from '../../lib/settingsPersist';
import { shouldResurfaceGuardianNudge } from '../../lib/guardianNudge';
import styles from './GuardianNudgeCard.module.css';

export function GuardianNudgeCard() {
  const { raw } = useAthleteContext();
  const { data: tier } = useUserTier();
  const rels = useRelationships();
  const { saveField } = useSettingsPersist();
  const [dismissed, setDismissed] = useState(false);

  const skippedAt =
    typeof raw._guardian_invite_skipped_at === 'string'
      ? raw._guardian_invite_skipped_at
      : null;
  const dismissedAt =
    typeof raw._guardian_nudge_dismissed_at === 'string'
      ? raw._guardian_nudge_dismissed_at
      : null;

  // Wait for the signals to settle before deciding (no flash).
  if (rels.loading || !tier) return null;
  if (dismissed) return null;

  // F-510 — key on the calendar day, not a module-load timestamp: a same-visit
  // reload keeps today's date, so the one-time resurface never re-arms mid-day;
  // the day after the skip (the reload-stable proxy for "next sign-in") does.
  const show = shouldResurfaceGuardianNudge({
    derivedMinor: tier.derivedMinor === true,
    hasAnyRelationship: rels.relationships.length > 0,
    skippedAt,
    dismissedAt,
    nowIso: new Date().toISOString(),
  });
  if (!show) return null;

  const onNotNow = async () => {
    setDismissed(true); // hide immediately; the stamp makes it permanent
    try {
      await saveField('_guardian_nudge_dismissed_at', new Date().toISOString());
    } catch {
      /* best-effort — the card is already hidden for this session */
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.title}>A parent in your corner?</div>
        <p className={styles.body}>
          You skipped this during setup. It&rsquo;s still your call. Two minutes
          if you want it.
        </p>
        <div className={styles.actions}>
          {/* Plain <a> with the FULL /v2 path — the export's deep link, the
              same idiom SubscriptionSection's guardian CTA uses (basename-
              independent; a router Link would need the bare path and diverge
              from the sibling surface). */}
          <a className={styles.invite} href="/v2/settings#guardians">
            Invite ›
          </a>
          <button type="button" className={styles.notNow} onClick={() => void onNotNow()}>
            Not now
          </button>
        </div>
      </div>
    </div>
  );
}
